import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Package, Plus, RotateCcw, Smile, Trash2 } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import ReferralCard from "@/components/ReferralCard";
import { CoverView } from "@/components/PageView";
import { api, errMsg, img, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STATUS_LABEL, reorderPath } from "@/pages/OrderPage";

function BookCard({ b, onDelete }) {
  const [photos, setPhotos] = useState({});
  useEffect(() => {
    if (b.cover_photo_id) api.get(`/books/${b.id}/photos`).then((r) => setPhotos(Object.fromEntries(r.data.map((p) => [p.id, p]))));
  }, [b.id, b.cover_photo_id]);
  const href = b.status === "ready" || b.status === "ordered" ? `/books/${b.id}/edit` : `/new/${b.id}`;
  return (
    <div className="group" data-testid={`book-card-${b.id}`}>
      <Link to={href} className="block">
        <div className="page-shadow mx-auto max-w-[260px] transition-transform duration-300 group-hover:-translate-y-1">
          {b.cover_photo_id ? (
            <CoverView book={b} photos={photos} enhance={b.enhance} small />
          ) : (
            <div className="grid aspect-square place-items-center bg-[#F5F0E8] text-[#8C8498]"><BookOpen strokeWidth={1.2} className="h-10 w-10" /></div>
          )}
        </div>
      </Link>
      <div className="mt-4 flex items-start justify-between gap-2">
        <div>
          <h3 className="font-serif text-xl leading-tight">{b.title}</h3>
          <p className="mt-1 text-xs text-[#8C8498]">{b.size} · {b.photo_count} photos · {b.status === "draft" ? "Draft" : b.status === "ordered" ? "Ordered" : `${b.page_count} pages`}</p>
        </div>
        <button data-testid={`book-delete-${b.id}`} onClick={() => onDelete(b)} className="p-1.5 text-[#8C8498] opacity-60 hover:text-rose-600 hover:opacity-100" aria-label="Delete book">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [books, setBooks] = useState(null);
  const [orders, setOrders] = useState([]);
  const [carics, setCarics] = useState([]);
  const load = () => {
    api.get("/books").then((r) => setBooks(r.data));
    api.get("/orders").then((r) => setOrders(r.data));
    api.get("/caricatures").then((r) => setCarics(r.data));
  };
  useEffect(load, []);
  const del = async (b) => {
    if (!window.confirm(`Delete "${b.title}"? This can't be undone.`)) return;
    try { await api.delete(`/books/${b.id}`); toast.success("Book deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="min-h-screen">
      <Nav />
      <main className="mx-auto max-w-7xl px-5 py-12 sm:px-8">
        <p className="overline">Namaste, {user?.name}</p>
        <h1 className="mt-2 font-serif text-4xl tracking-tight sm:text-5xl">Your books</h1>
        <div className="mt-10 grid grid-cols-2 gap-x-6 gap-y-10 sm:grid-cols-3 lg:grid-cols-4" data-testid="books-grid">
          <Link to="/new" data-testid="dashboard-new-book-card" className="grid aspect-square max-w-[260px] place-items-center rounded-sm border-2 border-dashed border-[#E8E2D9] text-[#8C8498] transition-colors hover:border-[#3B5BDB] hover:text-[#3B5BDB]">
            <span className="flex flex-col items-center gap-2 text-sm font-medium"><Plus className="h-6 w-6" />New book</span>
          </Link>
          {books?.map((b) => <BookCard key={b.id} b={b} onDelete={del} />)}
        </div>

        <h2 className="mt-20 font-serif text-3xl tracking-tight">Caricatures</h2>
        <div className="mt-6 grid grid-cols-2 gap-x-6 gap-y-10 sm:grid-cols-3 lg:grid-cols-4" data-testid="caricatures-grid">
          <Link to="/caricature" data-testid="dashboard-new-caricature-card" className="grid aspect-[3/4] max-w-[200px] place-items-center rounded-sm border-2 border-dashed border-[#E8E2D9] text-[#8C8498] transition-colors hover:border-[#3B5BDB] hover:text-[#3B5BDB]">
            <span className="flex flex-col items-center gap-2 text-center text-sm font-medium"><Smile className="h-6 w-6" />Make a caricature</span>
          </Link>
          {carics.map((c) => (
            <Link key={c.id} to={`/caricature/${c.id}`} data-testid={`caricature-card-${c.id}`} className="group">
              <div className="page-shadow max-w-[200px] overflow-hidden bg-white transition-transform duration-300 group-hover:-translate-y-1">
                {c.preview_url ? <img src={img(c.preview_url)} alt={c.name} className="aspect-[3/4] w-full object-cover" /> : (
                  <div className="grid aspect-[3/4] place-items-center bg-[#F5F0E8] text-xs text-[#8C8498]">{c.status === "failed" ? "Needs a retry" : "Drawing…"}</div>
                )}
              </div>
              <h3 className="mt-3 font-serif text-xl leading-tight">{c.name}</h3>
              <p className="mt-1 text-xs text-[#8C8498]">{c.traits.length} traits · {c.paid_order_id ? "Purchased" : c.status === "ready" ? "Ready" : c.status === "failed" ? "Failed" : "Generating"}</p>
            </Link>
          ))}
        </div>

        <h2 className="mt-20 font-serif text-3xl tracking-tight">Orders</h2>
        {orders.length === 0 ? (
          <p className="mt-4 text-sm text-[#8C8498]" data-testid="orders-empty">No orders yet — your first book is waiting.</p>
        ) : (
          <div className="mt-6 divide-y divide-[#E8E2D9] rounded-lg border border-[#E8E2D9] bg-white" data-testid="orders-list">
            {orders.map((o) => (
              <div key={o.id} className="flex items-center gap-2 px-5 py-4 hover:bg-[#FDFBF7]">
                <Link to={`/orders/${o.id}`} data-testid={`order-row-${o.id}`} className="flex min-w-0 flex-1 items-center justify-between gap-4">
                  <span className="flex min-w-0 items-center gap-3">
                    <Package className="h-5 w-5 shrink-0 text-[#3B5BDB]" strokeWidth={1.5} />
                    <span className="min-w-0">
                      <span className="block truncate font-medium">{o.book_title}</span>
                      <span className="text-xs text-[#8C8498]">{new Date(o.created_at).toLocaleDateString("en-IN")} · {inr(o.pricing.total)}</span>
                    </span>
                  </span>
                  <span className="shrink-0 rounded-full bg-[#F0F5F1] px-3 py-1 text-xs font-medium text-[#4F7A5C]">{STATUS_LABEL[o.status] || o.status}</span>
                </Link>
                {o.status !== "created" && reorderPath(o) && (
                  <Link to={reorderPath(o)} data-testid={`order-reorder-${o.id}`} title="Reorder with the same address" className="btn-ghost !px-2.5 !py-1.5 !text-xs"><RotateCcw className="h-3.5 w-3.5" /><span className="max-sm:hidden">Reorder</span></Link>
                )}
              </div>
            ))}
          </div>
        )}
        <ReferralCard />
      </main>
    </div>
  );
}
